import React, { useState, Fragment } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { ChevronDown, ChevronRight } from 'lucide-react';

const ZachetkaGroupsTab = ({ data, loading, error }) => {
    const [exporting, setExporting] = useState(false);
    const [expanded, setExpanded] = useState(() => new Set());

    if (loading) {
        return (
            <div className="loading">
                <div className="spinner" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="data-table-container">
                <h2>Группы и студенты</h2>
                <p style={{ color: '#b00020', marginTop: 16 }}>
                    {error.response?.data?.message || error.message || 'Не удалось загрузить данные из Зачётки'}
                </p>
            </div>
        );
    }

    const groups = data?.groups || [];
    const summary = data?.summary || { groups_count: 0, students_count: 0 };

    const toggle = (key) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleExport = () => {
        if (!groups.length) {
            toast.info('Нет данных для экспорта');
            return;
        }

        setExporting(true);
        try {
            const wb = XLSX.utils.book_new();

            const groupsHeaders = ['ID', 'Группа', 'Программа', 'Кол-во студентов'];
            const groupsRows = groups.map((g) => [
                g.id ?? '',
                g.name ?? '',
                g.program_title ?? '',
                g.students_count ?? 0
            ]);
            XLSX.utils.book_append_sheet(
                wb,
                XLSX.utils.aoa_to_sheet([groupsHeaders, ...groupsRows]),
                'Группы'
            );

            const studentsHeaders = ['ID группы', 'Группа', 'ID студента', 'ФИО', 'Email'];
            const studentsRows = groups.flatMap((g) =>
                (g.students || []).map((s) => [
                    g.id ?? '',
                    g.name ?? '',
                    s.id ?? '',
                    s.full_name ?? '',
                    s.email ?? ''
                ])
            );
            XLSX.utils.book_append_sheet(
                wb,
                XLSX.utils.aoa_to_sheet([studentsHeaders, ...studentsRows]),
                'Студенты'
            );

            XLSX.writeFile(wb, `группы_студенты_зачетка_${new Date().toISOString().slice(0, 10)}.xlsx`);
            toast.success('Экспорт сохранён');
        } catch (error) {
            console.error(error);
            toast.error('Ошибка экспорта');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="data-table-container">
            <div
                className="table-header"
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}
            >
                <div>
                    <h2>Группы и студенты</h2>
                    <p style={{ color: '#666', fontSize: '14px', marginTop: '10px' }}>
                        Групп: <strong>{summary.groups_count}</strong>
                        {' · '}
                        Студентов: <strong>{summary.students_count}</strong>
                    </p>
                </div>
                <button
                    type="button"
                    className="action-btn complete-btn"
                    onClick={handleExport}
                    disabled={exporting || !groups.length}
                    title="Скачать XLSX"
                >
                    {exporting ? 'Экспорт…' : 'Экспорт XLSX'}
                </button>
            </div>

            <table className="data-table">
                <thead>
                    <tr>
                        <th style={{ width: 40 }} />
                        <th>#</th>
                        <th>Группа</th>
                        <th>Программа</th>
                        <th>Студентов</th>
                    </tr>
                </thead>
                <tbody>
                    {groups.length > 0 ? (
                        groups.map((row, index) => {
                            const rowKey = `${row.id}-${index}`;
                            const isOpen = expanded.has(rowKey);
                            return (
                                <Fragment key={rowKey}>
                                    <tr>
                                        <td>
                                            <button
                                                type="button"
                                                className="action-btn pending-btn"
                                                onClick={() => toggle(rowKey)}
                                                title={isOpen ? 'Скрыть студентов' : 'Показать студентов'}
                                                aria-expanded={isOpen}
                                                style={{ padding: '4px 6px', lineHeight: 0 }}
                                            >
                                                {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                            </button>
                                        </td>
                                        <td>{index + 1}</td>
                                        <td>{row.name}</td>
                                        <td>{row.program_title || '—'}</td>
                                        <td>{row.students_count}</td>
                                    </tr>
                                    {isOpen && (
                                        <tr>
                                            <td colSpan={5} style={{ background: '#f8f9fa', padding: '12px 16px' }}>
                                                {(row.students || []).length > 0 ? (
                                                    <table className="data-table" style={{ margin: 0, background: '#fff' }}>
                                                        <thead>
                                                            <tr>
                                                                <th>#</th>
                                                                <th>ФИО</th>
                                                                <th>Email</th>
                                                                <th>ID</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {row.students.map((s, sIdx) => (
                                                                <tr key={s.id || `${row.id}-${sIdx}`}>
                                                                    <td>{sIdx + 1}</td>
                                                                    <td>{s.full_name}</td>
                                                                    <td>
                                                                        {s.email
                                                                            ? <a href={`mailto:${s.email}`}>{s.email}</a>
                                                                            : '—'}
                                                                    </td>
                                                                    <td>{s.id ?? '—'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <span style={{ color: '#888' }}>Нет студентов</span>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })
                    ) : (
                        <tr>
                            <td colSpan={5} style={{ textAlign: 'center', color: '#888' }}>
                                Нет данных
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default ZachetkaGroupsTab;
